import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SEO from "../seo";
import API from "../api/api";
import Sidebar from "../components/Sidebar";
import LatestArticlesSidebar from "../components/LatestArticlesSidebar";
import TagSidebar from "../components/TagSidebar";
import YouTubeVerticalAd from "../components/YouTubeVerticalAd";

export default function ArticlePage() {
  const { slug } = useParams();

  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);

  /* ================= FETCH ARTICLE ================= */
  useEffect(() => {
    if (!slug) return;

    setArticle(null);
    setRelated([]);

    API.get(`/api/articles/${slug}`)
      .then((res) => setArticle(res.data))
      .catch(() => setArticle(null));
  }, [slug]);

  /* ================= FETCH RELATED ================= */
  useEffect(() => {
    if (!article?.slug) return;

    API.get(`/api/articles/related/${article.slug}`)
      .then((res) =>
        setRelated(Array.isArray(res.data) ? res.data : [])
      )
      .catch(() => setRelated([]));
  }, [article]);

  if (!article) {
    return <div className="p-12 text-center">Loading article…</div>;
  }

  /* ================= HELPERS ================= */
  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "";

  const tags = Array.isArray(article.tags)
    ? article.tags.map((t) => t.replace(/^#/, "").trim()).filter(Boolean)
    : typeof article.tags === "string"
      ? article.tags.split(",").map((t) => t.replace(/^#/, "").trim()).filter(Boolean)
      : [];

  const pageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const author = article.author || {};
  const reactions = article.reactions || {};

  return (
    <>
      {/* ================= SEO ================= */}
      <SEO
        title={article.seo_title || article.title}
        description={
          article.meta_description ||
          article.excerpt ||
          "Latest sports news, analysis and match updates."
        }
      />

      <div className="max-w-7xl mx-auto mt-10 px-4 grid grid-cols-1 lg:grid-cols-3 gap-10">

        {/* ================= MAIN CONTENT ================= */}
        <div className="lg:col-span-2">

          {/* CATEGORY + SPORT */}
          <div className="flex gap-2">
            <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
              {article.category || "News"}
            </span>
            {article.sport && (
              <span className="bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                {article.sport}
              </span>
            )}
          </div>

          {/* TITLE */}
          <h1 className="mt-4 text-3xl lg:text-4xl font-extrabold leading-tight">
            {article.title}
          </h1>

          {/* META */}
          {(article.meta_description || article.excerpt) && (
            <p
              className="
      mt-3
      max-w-3xl
      text-base
      text-gray-600
      leading-relaxed
    "
            >
              {article.meta_description}
            </p>
          )}

          <p className="mt-2 text-sm text-gray-600">
            🗓 {formatDate(article.created_at)} · 👁 {article.views || 0} views
          </p>

          {/* ================= SHARE BAR (INLINE) ================= */}
          <div className="mt-4 flex items-center gap-3 border-y py-4">
            <span className="font-semibold text-gray-700">Share:</span>

            <a
              href={`https://facebook.com/sharer/sharer.php?u=${pageUrl}`}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center hover:scale-110 transition"
            >
              F
            </a>

            <a
              href={`https://twitter.com/intent/tweet?url=${pageUrl}`}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center hover:scale-110 transition"
            >
              X
            </a>

            <a
              href={`https://wa.me/?text=${pageUrl}`}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full bg-green-500 text-white flex items-center justify-center hover:scale-110 transition"
            >
              W
            </a>

            <button
              onClick={() => navigator.clipboard.writeText(pageUrl)}
              className="w-9 h-9 rounded-full bg-gray-600 text-white flex items-center justify-center hover:scale-110 transition"
            >
              ⧉
            </button>
          </div>

          {/* ================= HERO IMAGE ================= */}
          {article.images && (
            <figure className="relative mt-8 rounded-2xl overflow-hidden">
              <img
                src={article.images}
                alt={article.title}
                loading="lazy"
                className="w-full h-[260px] sm:h-[360px] lg:h-[420px] object-cover"
              />

              {article.image_credit && (
                <figcaption className="absolute bottom-2 right-3 text-[11px] text-gray-200 bg-black/50 px-2 py-[2px] rounded">
                  Image credit: {article.image_credit}
                </figcaption>
              )}
            </figure>
          )}

          {/* ================= CONTENT ================= */}
          <div
            className="prose prose-lg mt-8 max-w-[720px]"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* ================= REACTIONS (INLINE) ================= */}
          <div className="border-t pt-6 mt-6">
            <h3 className="font-semibold mb-3">Fan Reactions</h3>

            <div className="flex flex-wrap gap-3">
              {[
                ["like", "👍 Like"],
                ["love", "🔥 Love"],
                ["wow", "😮 Wow"],
                ["funny", "😂 Funny"],
                ["angry", "😡 Angry"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100
                             hover:bg-gray-200 text-sm font-semibold transition"
                >
                  {label}
                  <span className="bg-white px-2 py-0.5 rounded-full text-xs">
                    {reactions[key] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* ================= RELATED TOPICS (CARDS) ================= */}
          {related.length > 0 && (
            <div className="mt-12">
              <h3 className="text-2xl font-extrabold mb-6">
                Related Topics
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.slice(0, 6).map((item) => (
                  <Link
                    key={item.slug}
                    to={`/articles/${item.slug}`}
                    className="
            group
            rounded-2xl
            overflow-hidden
            bg-white
            border
            hover:shadow-xl
            transition
          "
                  >
                    {/* IMAGE */}
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={item.images || "/logo-blogysports.png"}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition"
                      />

                      {/* CATEGORY */}
                      <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        {item.category || "News"}
                      </span>
                    </div>

                    {/* CONTENT */}
                    <div className="p-4">
                      <h4 className="font-semibold text-sm line-clamp-2 group-hover:text-red-600 transition">
                        {item.title}
                      </h4>

                      {item.sport && (
                        <p className="text-xs text-gray-500 mt-1 uppercase">
                          {item.sport}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* ================= AUTHOR CARD ================= */}
          {author?.name && (
            <Link
              to={`/author/${author.slug || author.name.toLowerCase().replace(/\s+/g, "-")}`}
              state={{ author }}
              className="block mt-12 group"
            >
              <div
                className="
        flex items-center gap-5
        p-6
        rounded-2xl
        border border-red-200
        bg-gradient-to-r from-red-50 via-white to-white
        hover:shadow-xl
        transition
      "
              >
                {/* AUTHOR IMAGE */}
                <div className="relative">
                  <img
                    src={author.image}
                    alt={author.name}
                    className="
            w-20 h-20
            rounded-full
            object-cover
            border-4 border-white
            shadow-md
          "
                  />

                  {/* VERIFIED BADGE (OPTIONAL) */}
                  <span className="absolute -bottom-1 -right-1 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                    ✍️
                  </span>
                </div>

                {/* AUTHOR INFO */}
                <div className="flex-1">
                  <h3 className="text-xl font-extrabold text-gray-900 group-hover:text-red-600 transition">
                    {author.name}
                  </h3>

                  {author.designation && (
                    <p className="text-sm text-gray-600 mt-0.5">
                      {author.designation}
                    </p>
                  )}

                  {author.bio && (
                    <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                      {author.bio}
                    </p>
                  )}

                  <span className="inline-block mt-3 text-sm font-semibold text-red-600">
                    View full profile →
                  </span>
                </div>
              </div>
            </Link>
          )}

        </div>

        {/* ================= SIDEBAR ================= */}
        <div className="hidden lg:block space-y-8">
          <div className="sticky top-28 space-y-8">
            <Sidebar />
            <LatestArticlesSidebar />
            {tags.length > 0 && <TagSidebar tags={tags} />}
          </div>
          <div>
            <YouTubeVerticalAd />
          </div>
        </div>
      </div>
    </>
  );
}
